import React, { memo, useMemo } from "react";
import { round } from "lodash";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import dayjs from "dayjs";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DoNotTouchOutlinedIcon from "@mui/icons-material/DoNotTouchOutlined";
import ReleaseStatusChip from "../../ManagePlanograms/ReleaseStatusChip";
import CustomDropdown from "../../../components/CustomDropdown";
import NumericLabel from "../../../components/NumericLabel";
import {
  singleValue,
  getFootageValue,
  getFormattedNumber,
  prettyNumericRound,
  orderStatus,
  getIndicators,
} from "../../../utils";
import config from "../../../config";
import { validPog } from "../../../utils/pogValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import SSPogLoader from "./inlineLoader";

const SSRestrictedIconSx = {
  fontSize: "18px",
  color: "#ff5722",
  marginRight: "5px",
};

const SSPogRowSx = (
  loading = true,
  pogBlocked = false,
  isSelected = false,
  isDuplicate = false,
  isReleased = false
) => ({
  fontSize: "0.75rem",
  marginTop: "2px",
  marginBottom: "2px",
  backgroundColor:
    !loading && (pogBlocked || isReleased || isDuplicate)
      ? "#00000014"
      : "white",
  boxShadow: !loading
    ? getIndicators(
        false,
        false,
        isSelected,
        isReleased,
        false,
        isDuplicate,
        false
      )
    : "",
  "& .ss-pog-title": {
    fontWeight: 600,
  },
});

const InlineLabelStyle = {
  fontWeight: 500,
};

const SSPogOrderRow = { ml: 4.5, mb: 1, height: "24px", alignItems: "center" };

const SSPogSaveIconSx = (disabled = false) => ({
  color: "primary.main",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? "0.5" : "1",
});

const SSTruncateText = {
  display: "inline",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

function SSPog({
  pog = {},
  handleProcessData = () => {},
  handleInlineSave = () => {},
  freezePog = false,
  isDuplicateSelection = false,
}) {
  const isPogChecked = pog?.selection;
  const isPogSelected = pog?.Selected;

  const isPartOfItemsDept = isItemDepartment(pog);

  const blockPog =
    !validPog(pog) ||
    pog?.CustomReleaseStatus || // if item has at least 1 item released then checkbox and dropdown of Y freezes
    freezePog ||
    isDuplicateSelection;

  const handleChange =
    (field = "", isChecked = false, isSelected = false) =>
    (event) => {
      const value =
        event.target.type !== "checkbox"
          ? event?.target?.value
          : event?.target?.checked;
      handleProcessData({
        field,
        value,
        isPogChecked: isChecked,
        isPogSelected: isSelected,
      });
    };

  const handleSave = () => {
    if (blockPog) return false;
    handleInlineSave(pog);
  };

  const isReleased = useMemo(
    () => pog?.ReleaseStatus === orderStatus.released && !pog?.virtualLoading,
    [pog?.ReleaseStatus, pog?.virtualLoading]
  );

  const isRestricted = useMemo(() => {
    return pog?.RestrictedInd;
  }, [pog?.RestrictedInd]);

  return (
    <Grid
      container
      sx={SSPogRowSx(
        pog?.virtualLoading,
        blockPog && !freezePog,
        pog?.selection || pog?.Selected === singleValue.yes,
        isDuplicateSelection,
        isReleased
      )}
    >
      {!pog?.virtualLoading ? (
        <>
          <Grid
            container
            columnGap={0.5}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={0.2}>
              <Checkbox
                size="small"
                disableRipple
                checked={pog?.selection}
                onClick={handleChange("selection", isPogChecked, isPogSelected)}
                disabled={blockPog}
              />
            </Grid>
            <Grid item xs={0.5} className="ss-pog-title">
              {!isPartOfItemsDept ? (
                <Link
                  href={config.PogFileBasePath.concat(pog?.PlanogramPDF)}
                  target="_blank"
                  rel="noreferrer noopener"
                  mr={0.2}
                >
                  {pog?.PlanogramNumber}
                </Link>
              ) : (
                <Link underline="none">{pog?.PlanogramNumber}</Link>
              )}
            </Grid>
            <Grid item xs={isRestricted ? 1.3 : 1.75} sx={SSTruncateText}>
              <Tooltip title={pog?.PlanogramName}>
                <span className="ss-pog-title">
                  <Link
                    href={`/store-supplies/items/${pog?.DepartmentLetter}/${pog?.PlanogramNumber}`}
                    ml={0.2}
                  >
                    {!isPartOfItemsDept
                      ? `${getFootageValue(pog?.LinealFootage)}-${
                          pog?.PlanogramName
                        }`
                      : `${pog?.PlanogramName}`}
                  </Link>
                </span>
              </Tooltip>
            </Grid>
            {isRestricted && (
              <>
                <Grid
                  item
                  xs="auto"
                  md={0.3}
                  lg={0.3}
                  className="pog-title"
                  mr={0.5}
                >
                  {pog?.RestrictedInd && (
                    <Tooltip title="Restricted" arrow>
                      <DoNotTouchOutlinedIcon sx={SSRestrictedIconSx} />
                    </Tooltip>
                  )}
                </Grid>
              </>
            )}
            <Grid item xs={1.5} sx={SSTruncateText}>
              <Tooltip title={pog?.DepartmentName}>
                <span>{pog?.DepartmentName}</span>
              </Tooltip>
            </Grid>
            <Grid item xs={1.5} sx={SSTruncateText}>
              <Tooltip title={pog?.Category}>
                <span>{pog?.Category}</span>
              </Tooltip>
            </Grid>
            <Grid item xs={0.2}>
              <span>{!Boolean(pog?.OSOSelected) ? "No" : "Yes"}</span>
            </Grid>
            <Grid item xs={0.5}>
              <CustomDropdown
                value={pog?.Selected}
                inputsx={{ fontSize: "10px" }}
                onChange={handleChange("Selected", isPogChecked, isPogSelected)}
                disabled={blockPog || isPartOfItemsDept}
              >
                <option value="N">N</option>
                <option value="Y">Y</option>
              </CustomDropdown>
            </Grid>
            <Grid item xs={0.5}>
              <span>
                {pog?.SelectedItems ?? 0}/{pog?.TotalItems ?? 0}
              </span>
            </Grid>
            <Grid item xs={0.5}>
              <NumericLabel isSeparator value={round(pog?.Qty || 0, 2)} />
            </Grid>
            <Grid item xs={0.7}>
              <span>${getFormattedNumber(prettyNumericRound(pog?.Cost))}</span>
            </Grid>
            <Grid item xs={0.7}>
              <span>
                ${getFormattedNumber(prettyNumericRound(pog?.AcqCost || 0))}
              </span>
            </Grid>
            <Grid item xs={0.5}>
              <NumericLabel isSeparator value={round(pog?.AdjQty || 0, 2)} />
            </Grid>
            <Grid item xs={0.7}>
              <span>
                ${getFormattedNumber(prettyNumericRound(pog?.AdjCost || 0))}
              </span>
            </Grid>
            <Grid item xs={0.4}>
              <Tooltip title="Save" arrow>
                <SaveOutlinedIcon
                  size="small"
                  sx={SSPogSaveIconSx(blockPog)}
                  onClick={handleSave}
                  disabled={blockPog}
                />
              </Tooltip>
            </Grid>
          </Grid>
          <Grid
            container
            columnGap={0.5}
            alignItems="center"
            justifyContent="flex-start"
            sx={SSPogOrderRow}
          >
            <Grid item xs={1.5}>
              <span style={InlineLabelStyle}>PO Number:</span>{" "}
              <span>{!!pog?.PONumber ? pog?.PONumber : "-"}</span>
            </Grid>
            <Grid item xs={1.5}>
              <span style={InlineLabelStyle}>Release Date:</span>{" "}
              <span>
                {!!pog?.ReleaseDate
                  ? dayjs(pog?.ReleaseDate).format("MM/DD/YYYY")
                  : "-"}
              </span>
            </Grid>
            <Grid item xs={2}>
              <span style={InlineLabelStyle}>Release Status:</span>{" "}
              <span>
                {!!pog?.ReleaseStatus ? (
                  <ReleaseStatusChip
                    status={pog?.ReleaseStatus === orderStatus.released}
                  />
                ) : (
                  "-"
                )}
              </span>
            </Grid>
            <Grid item xs={2} sx={SSTruncateText}>
              <span style={InlineLabelStyle}>Region:</span>{" "}
              <Tooltip title={!!pog?.Region ? pog?.Region : "-"}>
                <span>{!!pog?.Region ? pog?.Region : "-"}</span>
              </Tooltip>
            </Grid>
            <Grid item xs={2} sx={SSTruncateText}>
              <span style={InlineLabelStyle}>Urbanicity:</span>{" "}
              <Tooltip title={!!pog?.Urbanicity ? pog?.Urbanicity : "-"}>
                <span>{!!pog?.Urbanicity ? pog?.Urbanicity : "-"}</span>
              </Tooltip>
            </Grid>
          </Grid>
        </>
      ) : (
        <SSPogLoader />
      )}
    </Grid>
  );
}

export default memo(SSPog);
